import {
  AppSettings,
  PersistedDocument,
  StoredState,
  FontFamily,
  MirrorMode,
  MarkerSide,
  TextAlign,
  TextColumnWidth,
  TextColorPreset,
  DirectionMode,
  PromptMode,
} from './types';
import {
  CURRENT_SCHEMA_VERSION,
  DEFAULT_SETTINGS,
  DEFAULT_DOCUMENT,
} from './defaults';

function clamp(val: unknown, min: number, max: number, fallback: number): number {
  if (typeof val !== 'number' || Number.isNaN(val)) return fallback;
  return Math.min(max, Math.max(min, val));
}

function oneOf<T extends string>(val: unknown, allowed: readonly T[], fallback: T): T {
  if (typeof val === 'string' && (allowed as readonly string[]).includes(val)) {
    return val as T;
  }
  return fallback;
}

const FONT_FAMILIES: readonly FontFamily[] = ['sans', 'naskh', 'amiri'];
const MIRROR_MODES: readonly MirrorMode[] = ['off', 'horizontal', 'vertical', 'both'];
const MARKER_SIDES: readonly MarkerSide[] = ['auto', 'left', 'right'];
const TEXT_ALIGNS: readonly TextAlign[] = ['start', 'center'];
const COLUMN_WIDTHS: readonly TextColumnWidth[] = ['narrow', 'normal', 'wide'];
const COLOR_PRESETS: readonly TextColorPreset[] = ['white', 'warm', 'yellow'];
const DIRECTION_MODES: readonly DirectionMode[] = ['auto', 'rtl', 'ltr'];
const PROMPT_MODES: readonly PromptMode[] = ['manual', 'voice'];

/**
 * Validates and clamps settings, restoring defaults for any corrupted or missing fields
 */
export function validateSettings(raw: unknown): AppSettings {
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_SETTINGS };
  }

  const s = raw as Record<string, unknown>;

  return {
    fontSize: clamp(s.fontSize, 24, 400, DEFAULT_SETTINGS.fontSize),
    fontFamily: oneOf(s.fontFamily, FONT_FAMILIES, DEFAULT_SETTINGS.fontFamily),
    lineHeight: clamp(s.lineHeight, 1.2, 2.2, DEFAULT_SETTINGS.lineHeight),
    textAlign: oneOf(s.textAlign, TEXT_ALIGNS, DEFAULT_SETTINGS.textAlign),
    textColumnWidth: oneOf(s.textColumnWidth, COLUMN_WIDTHS, DEFAULT_SETTINGS.textColumnWidth),
    textColorPreset: oneOf(s.textColorPreset, COLOR_PRESETS, DEFAULT_SETTINGS.textColorPreset),
    directionMode: oneOf(s.directionMode, DIRECTION_MODES, DEFAULT_SETTINGS.directionMode),

    speed: clamp(s.speed, 2, 1500, DEFAULT_SETTINGS.speed),
    keepPaceOnFontChange: typeof s.keepPaceOnFontChange === 'boolean' ? s.keepPaceOnFontChange : DEFAULT_SETTINGS.keepPaceOnFontChange,
    countdownSeconds: clamp(s.countdownSeconds, 0, 10, DEFAULT_SETTINGS.countdownSeconds),
    modePreference: oneOf(s.modePreference, PROMPT_MODES, DEFAULT_SETTINGS.modePreference),

    mirrorMode: oneOf(s.mirrorMode, MIRROR_MODES, DEFAULT_SETTINGS.mirrorMode),
    markerEnabled: typeof s.markerEnabled === 'boolean' ? s.markerEnabled : DEFAULT_SETTINGS.markerEnabled,
    markerColor: typeof s.markerColor === 'string' && /^#[0-9a-fA-F]{6}$/.test(s.markerColor) ? s.markerColor : DEFAULT_SETTINGS.markerColor,
    markerWidth: clamp(s.markerWidth, 1, 16, DEFAULT_SETTINGS.markerWidth),
    markerOffset: clamp(s.markerOffset, 0, 100, DEFAULT_SETTINGS.markerOffset),
    markerOpacity: clamp(s.markerOpacity, 0.0, 1.0, DEFAULT_SETTINGS.markerOpacity),
    markerSide: oneOf(s.markerSide, MARKER_SIDES, DEFAULT_SETTINGS.markerSide),

    focusAreaEnabled: typeof s.focusAreaEnabled === 'boolean' ? s.focusAreaEnabled : DEFAULT_SETTINGS.focusAreaEnabled,
    focusAreaPosition: clamp(s.focusAreaPosition, 10, 90, DEFAULT_SETTINGS.focusAreaPosition),
    focusAreaHeight: clamp(s.focusAreaHeight, 5, 50, DEFAULT_SETTINGS.focusAreaHeight),
    focusAreaIntensity: clamp(s.focusAreaIntensity, 0.0, 0.3, DEFAULT_SETTINGS.focusAreaIntensity),

    focusLinesEnabled: typeof s.focusLinesEnabled === 'boolean' ? s.focusLinesEnabled : DEFAULT_SETTINGS.focusLinesEnabled,
    focusLinesThickness: clamp(s.focusLinesThickness, 1, 3, DEFAULT_SETTINGS.focusLinesThickness),
    focusLinesOpacity: clamp(s.focusLinesOpacity, 0.0, 1.0, DEFAULT_SETTINGS.focusLinesOpacity),

    focusMaskEnabled: typeof s.focusMaskEnabled === 'boolean' ? s.focusMaskEnabled : DEFAULT_SETTINGS.focusMaskEnabled,
    focusMaskOpacity: clamp(s.focusMaskOpacity, 0.0, 0.9, DEFAULT_SETTINGS.focusMaskOpacity),

    micDeviceId: typeof s.micDeviceId === 'string' ? s.micDeviceId : '',
    voiceSensitivity: clamp(s.voiceSensitivity, 0.1, 3.0, DEFAULT_SETTINGS.voiceSensitivity),
    voiceProvisionalEnabled: typeof s.voiceProvisionalEnabled === 'boolean' ? s.voiceProvisionalEnabled : DEFAULT_SETTINGS.voiceProvisionalEnabled,

    diagnosticsEnabled: typeof s.diagnosticsEnabled === 'boolean' ? s.diagnosticsEnabled : DEFAULT_SETTINGS.diagnosticsEnabled,
  };
}

/**
 * Validates document record, maintaining user code points exactly
 */
export function validateDocument(raw: unknown): PersistedDocument {
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_DOCUMENT };
  }

  const d = raw as Record<string, unknown>;

  return {
    script: typeof d.script === 'string' ? d.script : DEFAULT_DOCUMENT.script,
    lastClearedScript: typeof d.lastClearedScript === 'string' ? d.lastClearedScript : '',
    updatedAt: typeof d.updatedAt === 'number' ? d.updatedAt : Date.now(),
  };
}

/**
 * Migrates and normalizes stored state across schema versions
 */
export function migrateAndValidateState(raw: unknown): StoredState {
  if (!raw || typeof raw !== 'object') {
    return {
      schemaVersion: CURRENT_SCHEMA_VERSION,
      settings: { ...DEFAULT_SETTINGS },
      document: { ...DEFAULT_DOCUMENT },
    };
  }

  const state = raw as Record<string, unknown>;
  const version = typeof state.schemaVersion === 'number' ? state.schemaVersion : 0;

  // Schema migrations table (version 0 -> 1, etc.)
  if (version < 1) {
    // initial version setup
  }

  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    settings: validateSettings(state.settings),
    document: validateDocument(state.document),
  };
}
